import AdyenCheckout from '@adyen/adyen-web';
import { GooglePayProps } from '@adyen/adyen-web/dist/types/components/GooglePay/types';
import { PaymentMethodsResponse } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types';
import { PaymentMethods } from '@adyen/adyen-web/dist/types/types';
import { TFunction } from 'i18next';
import { useCallback, useEffect } from 'react';

import { normalizeLocaleForAdyen } from '@config/Adyen.static';
import { GOOGLE_PAY_CONTAINER_ID } from '@config/consts';
import {
  GooglePayState,
  GooglePaySubmitFn,
} from '@features/GooglePay/GooglePayButton/GooglePayButton.static';
import { Locale } from '@types-internal';
import { AdyenConfig } from '@types-internal/AdyenTypes';
import { safeStringify } from '@utils/utils';

export interface AdyenGooglePayConfig extends AdyenConfig {
  locale: Locale;
  t: TFunction;
  countryCode: string;
  amount: number;
  paymentMethodsResponse: PaymentMethodsResponse;
  onSubmit: GooglePaySubmitFn;
  onFatalError: (error: unknown) => void;
  buttonColor: GooglePayProps['buttonColor'];
  buttonType: GooglePayProps['buttonType'];
  siteId?: number;
  sid?: string;
  currency: string;
  customerId?: string;
  customerType?: string;
}

const useGooglePay = (config: AdyenGooglePayConfig) => {
  const {
    adyenEnvironment,
    clientKey,
    locale,
    countryCode,
    currency,
    amount,
    paymentMethodsResponse,
    onSubmit,
    onFatalError,
    buttonColor,
    buttonType,
  } = config;

  const onGooglePayButtonClicked = useCallback((resolve: () => void) => {
    resolve();
  }, []);

  const onGooglePaySubmitted = useCallback(
    (state: GooglePayState) => {
      const { googlePayToken, checkoutAttemptId, googlePayCardNetwork } =
        state?.data?.paymentMethod || {};

      if (googlePayToken) {
        onSubmit({ googlePayToken, checkoutAttemptId, googlePayCardNetwork });
      } else {
        onFatalError(
          new Error(`Failed to extract the googlePayToken from ${safeStringify(state)}`),
        );
      }
    },
    [onFatalError, onSubmit],
  );

  useEffect(() => {
    let googlePayComponent: InstanceType<PaymentMethods['googlepay']> | null = null;

    async function initialize() {
      try {
        const adyenLocale = normalizeLocaleForAdyen(locale);
        const adyen = await AdyenCheckout({
          environment: adyenEnvironment,
          locale: adyenLocale,
          paymentMethodsResponse,
          clientKey,
        });

        googlePayComponent = adyen.create('googlepay', {
          onSubmit: onGooglePaySubmitted,
          amount: {
            value: amount,
            currency,
          },
          onError: (error: unknown) => {
            onFatalError(error);
          },
          onClick: onGooglePayButtonClicked,
          countryCode,
          buttonColor,
          buttonType,
        }) as unknown as InstanceType<PaymentMethods['googlepay']>;

        await googlePayComponent.isAvailable();
        googlePayComponent.mount(`#${GOOGLE_PAY_CONTAINER_ID}`);
      } catch (error) {
        onFatalError(error);
      }
    }

    initialize();

    return () => {
      if (googlePayComponent) {
        googlePayComponent.unmount();
      }
    };
  }, [
    adyenEnvironment,
    locale,
    clientKey,
    countryCode,
    amount,
    currency,
    paymentMethodsResponse,
    onSubmit,
    onFatalError,
    buttonType,
    buttonColor,
  ]);
};

export default useGooglePay;
